
import * as THREE from "../../../libs/three.js/build/three.module.js";

// Pulls real point-cloud points out of the loaded octree(s) within a search
// cylinder around the pipe's direction segment, for the Pipe tool's cylinder
// fit (see cylinderFit.js) to run on.
//
// Deliberately scoped to *already-loaded* octree nodes only (never calls
// node.load()): the user can only have placed direction/seed points by
// clicking on point-cloud geometry that was actually rendered on screen at
// the time, which means the octree nodes covering that region are already
// resident at an appropriate LOD. Potree's own bulk-region query
// (ProfileRequest, see src/ProfileRequest.js) additionally loads missing
// nodes via an async priority queue spread across frames specifically to
// support querying regions the user hasn't looked at - not needed here,
// since by construction the pipe surface the tool needs was already visible.
// The per-node traversal/transform/rejection logic below mirrors
// ProfileRequest's own (node.boundingBox/.boundingSphere, the
// pointcloud.matrixWorld * translate(node.boundingBox.min) point transform,
// bounding-sphere-vs-region pruning) so it stays correct on the same octree
// format.
export function gatherPipeCandidates(pointclouds, directionA, directionB, searchRadius, extensionFraction = 0.15){
	const axisDir = directionB.clone().sub(directionA);
	const axisLength = axisDir.length();
	if(axisLength < 1e-6) return [];
	axisDir.normalize();

	const extension = axisLength * extensionFraction;
	const segStart = directionA.clone().addScaledVector(axisDir, -extension);
	const segEnd = directionB.clone().addScaledVector(axisDir, extension);

	const results = [];

	for(const pointcloud of pointclouds){
		if(!pointcloud.visible || !pointcloud.pcoGeometry || !pointcloud.pcoGeometry.root) continue;

		const root = pointcloud.pcoGeometry.root;
		const stack = [root];

		while(stack.length > 0){
			const node = stack.pop();
			if(!node || !node.loaded) continue;

			if(!nodeIntersectsSearchRegion(node, pointcloud.matrixWorld, segStart, segEnd, searchRadius)) continue;

			collectFromNode(node, pointcloud, segStart, segEnd, searchRadius, results);

			for(let i = 0; i < 8; i++){
				const child = node.children && node.children[i];
				if(child) stack.push(child);
			}
		}
	}

	return results;
}

function nodeIntersectsSearchRegion(node, matrixWorld, segStart, segEnd, searchRadius){
	const bbWorld = node.boundingBox.clone().applyMatrix4(matrixWorld);
	const bsWorld = bbWorld.getBoundingSphere(new THREE.Sphere());

	const line = new THREE.Line3(segStart, segEnd);
	const closest = line.closestPointToPoint(bsWorld.center, true, new THREE.Vector3());
	const distance = closest.distanceTo(bsWorld.center);

	return distance < (bsWorld.radius + searchRadius);
}

function collectFromNode(node, pointcloud, segStart, segEnd, searchRadius, out){
	const numPoints = node.numPoints;
	const geometry = node.geometry;
	if(!numPoints || !geometry || !geometry.attributes || !geometry.attributes.position) return;

	const nodeMatrix = new THREE.Matrix4().makeTranslation(...node.boundingBox.min.toArray());
	const matrix = new THREE.Matrix4().multiplyMatrices(pointcloud.matrixWorld, nodeMatrix);

	const view = geometry.attributes.position.array;
	const line = new THREE.Line3(segStart, segEnd);
	const pos = new THREE.Vector3();
	const closest = new THREE.Vector3();

	for(let i = 0; i < numPoints; i++){
		pos.set(view[i * 3], view[i * 3 + 1], view[i * 3 + 2]);
		pos.applyMatrix4(matrix);

		line.closestPointToPoint(pos, true, closest);
		if(closest.distanceTo(pos) <= searchRadius){
			out.push(pos.clone());
		}
	}
}
